import React, {useState} from 'react';
import {Card} from 'antd';
import {PackDataEnhancedRow} from '@src/components/UniversalExecutor/PackDataEnhancedRow';
import PackDataExpandedRow, {tryParseToJSON} from './PackDataExpandedRow';
import PipelinePanel from './PipelinePanel';
import usePackPipeline from './usePackPipeline';


interface PackPipelineDetailProps {
    service_item_id: number;
    service_id?: number;
    /* Who is looking — the same pipeline is shown to a consumer and to a member
       of the owning team whose role does not let them run it. Required rather
       than defaulted: forgetting to say should not quietly mean "may edit". */
    readOnly: boolean;
    pov?: string;
}

const PackPipelineDetail = ({ service_item_id, service_id, readOnly, pov }: PackPipelineDetailProps) => {
    const {
        current, setCurrent,
        isEditing, setIsEditing,
        compareMode, setCompareMode,
        setComparePipelineID,
        currentPackData, comparePackData,
        pipelineOptions, displayPipelineID, displayComparePipelineID,
        dataPipeline, dataComparePipeline,
        currentAIProcessorResponse, compareAIProcessorResponse, genUIData,
        universalExecutorEnabled,
        handlePipelineChange, triggerPackData,
        triggerAIProcessor, retriggerAIProcessor, toggleAppliedStatus,
    } = usePackPipeline(service_item_id, service_id, { pov, readOnly });

    const [viewMode, setViewMode] = useState<'enhanced' | 'raw'>('enhanced');
    const [editedJson, setEditedJson] = useState<string>('');

    const startEditing = () => {
        const parsed = tryParseToJSON(currentPackData?.data);
        setEditedJson(typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2));
        setIsEditing(true);
    };

    return (
        <>
            {/* One panel: which pipeline, which stage, and what to do with it —
                including, when comparing, what each of the two runs was. */}
            <PipelinePanel
                pipelineOptions={pipelineOptions}
                displayPipelineID={displayPipelineID}
                displayComparePipelineID={displayComparePipelineID}
                compareMode={compareMode}
                handlePipelineChange={handlePipelineChange}
                setCompareMode={setCompareMode}
                setComparePipelineID={setComparePipelineID}
                current={current}
                onStageChange={(value) => setCurrent(value as -1|0|1|2)}
                dataPipeline={dataPipeline}
                showOverview={universalExecutorEnabled}
                toggleAppliedStatus={toggleAppliedStatus}
                triggerAIProcessor={triggerAIProcessor}
                retriggerAIProcessor={retriggerAIProcessor}
                aiProcessorResponse={currentAIProcessorResponse}
                compareAIProcessorResponse={compareAIProcessorResponse}
                dataComparePipeline={dataComparePipeline}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isEditing={isEditing}
                onEdit={startEditing}
                onSubmitEdit={() => {
                    triggerPackData(editedJson);
                    setIsEditing(false);
                }}
                onCancelEdit={() => setIsEditing(false)}
                switchVisible={universalExecutorEnabled || !!genUIData}
                switchLabels={universalExecutorEnabled ? undefined : { enhanced: 'Generative UI', raw: 'Raw Data' }}
                showDetails={current !== -1}
                readOnly={readOnly}
            />

            {/* Body data card — both sides name themselves in the diff, and both
                sides' run details are in the panel's stage drawer above */}
            <Card>
                {universalExecutorEnabled ? (
                    <PackDataEnhancedRow
                        initialValue={currentPackData?.data}
                        compareValue={comparePackData?.data}
                        isCompareMode={compareMode}
                        currentStage={current}
                        dataPipeline={dataPipeline}
                        dataComparePipeline={dataComparePipeline}
                        isEditing={isEditing}
                        editedJson={editedJson}
                        onEditedJsonChange={setEditedJson}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        readOnly={readOnly}
                    />
                ) : (
                    <PackDataExpandedRow
                        initialValue={currentPackData?.data}
                        compareValue={comparePackData?.data}
                        isCompareMode={compareMode}
                        dataPipeline={dataPipeline}
                        dataComparePipeline={dataComparePipeline}
                        isEditing={isEditing}
                        editedJson={editedJson}
                        onEditedJsonChange={setEditedJson}
                        viewMode={viewMode}
                        genUIData={genUIData}
                        readOnly={readOnly}
                    />
                )}
            </Card>
        </>
    );
};

export default PackPipelineDetail;
